// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/__tests__/activityGrouping.test.ts
import {
  KnowledgeActivityAction,
  KnowledgeActivityActorKind,
  type KnowledgeActivityResult,
} from "../../../../utils/parsers/knowledge";

/**
 * Collapses workflow rule bursts in the activity feed. Processing one
 * document records one activity row per extracted rule, so the feed drowns
 * in "Puntt created the rule …" x60 — the audit trail keeps every row, but
 * the feed shows one grouped entry ("extracted 60 rules") that expands back
 * to the individual rows on demand.
 *
 * Only consecutive WORKFLOW-actor rows with the same groupable action are
 * collapsed. User actions (promote/reject/scope edits) always stay
 * individual, and a burst is only a burst when adjacent rows are close in
 * time — two extraction runs hours apart stay separate groups.
 */

/** Actions the extraction workflow emits once per rule in a batch. */
const GROUPABLE_ACTIONS: ReadonlySet<KnowledgeActivityAction> = new Set([
  KnowledgeActivityAction.RULE_CREATED,
  KnowledgeActivityAction.RULE_UPDATED,
]);

/** Adjacent rows further apart than this are different batches. */
const MAX_ADJACENT_GAP_MS = 30 * 60 * 1000;

const MIN_GROUP_SIZE = 2;

export type ActivityFeedItem =
  | { kind: "single"; activity: KnowledgeActivityResult }
  | {
      kind: "group";
      /** Stable key for expansion state — the newest member's id. */
      id: string;
      action: KnowledgeActivityAction;
      /** Newest-first, same order they arrived in. */
      activities: KnowledgeActivityResult[];
      /** Newest member's timestamp — the group renders one time, not a range. */
      createdAt: string;
    };

/** "Puntt extracted 60 rules" / "Puntt added evidence to 12 rules". */
export function groupSentence(
  action: KnowledgeActivityAction,
  count: number,
): string {
  const noun = count === 1 ? "rule" : "rules";
  if (action === KnowledgeActivityAction.RULE_UPDATED) {
    return `added evidence to ${count} ${noun}`;
  }
  return `extracted ${count} ${noun}`;
}

function isGroupable(activity: KnowledgeActivityResult): boolean {
  return (
    activity.actor.kind === KnowledgeActivityActorKind.WORKFLOW &&
    GROUPABLE_ACTIONS.has(activity.action)
  );
}

function closeInTime(
  a: KnowledgeActivityResult,
  b: KnowledgeActivityResult,
): boolean {
  const ta = new Date(a.createdAt).getTime();
  const tb = new Date(b.createdAt).getTime();
  if (Number.isNaN(ta) || Number.isNaN(tb)) return false;
  return Math.abs(ta - tb) <= MAX_ADJACENT_GAP_MS;
}

/**
 * Input is the feed as rendered: newest first. Runs of groupable rows with
 * the same action collapse into one group item; everything else passes
 * through in order.
 */
export function groupActivities(
  activities: KnowledgeActivityResult[],
): ActivityFeedItem[] {
  const items: ActivityFeedItem[] = [];
  let index = 0;

  while (index < activities.length) {
    const head = activities[index];
    if (!isGroupable(head)) {
      items.push({ kind: "single", activity: head });
      index += 1;
      continue;
    }

    const run: KnowledgeActivityResult[] = [head];
    let cursor = index + 1;
    while (
      cursor < activities.length &&
      isGroupable(activities[cursor]) &&
      activities[cursor].action === head.action &&
      closeInTime(activities[cursor - 1], activities[cursor])
    ) {
      run.push(activities[cursor]);
      cursor += 1;
    }

    if (run.length >= MIN_GROUP_SIZE) {
      items.push({
        kind: "group",
        id: head._id,
        action: head.action,
        activities: run,
        createdAt: head.createdAt,
      });
    } else {
      items.push({ kind: "single", activity: head });
    }
    index = cursor;
  }

  return items;
}
